import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { Spin, DatePicker } from "antd";
import dayjs, { Dayjs } from "dayjs";
import * as recruitmentApi from "../../../../../core/data/redux/services/recruitmentApi";
import GuestAuthModal from "../common/GuestAuthModal";
import { hexAlpha } from "../../types";
import { notify } from "../../../../../services/toast.config";

interface Resume {
  id: string;
  file_name: string;
  is_primary: boolean;
  uploaded_at: string;
  resume_url: string;
}

interface ScreeningQuestion {
  type: "text" | "multiple_choice";
  question: string;
  options: string[];
  required: boolean;
}

interface FormState {
  full_name: string;
  email: string;
  phone: string;
  current_company: string;
  experience_years: string;
  current_ctc: string;
  expected_ctc: string;
  notice_period_days: string;
  earliest_joining_date: string;
  cover_letter: string;
}

interface ApplySidebarProps {
  jobId: string;
  job?: any;
  companyInfo: { company_id: string; company_type: string };
  careerPageUuid: string;
  primaryColor?: string;
}

const EMPTY_FORM: FormState = {
  full_name: "",
  email: "",
  phone: "",
  current_company: "",
  experience_years: "",
  current_ctc: "",
  expected_ctc: "",
  notice_period_days: "",
  earliest_joining_date: "",
  cover_letter: "",
};


const cardStyle: React.CSSProperties = {
  background: "#fff",
  borderRadius: 14,
  border: "1px solid #e8eaed",
  boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
  overflow: "hidden",
};

const inputStyle = (disabled: boolean, primary: string): React.CSSProperties => ({
  width: "100%",
  padding: "7px 10px",
  borderRadius: 7,
  border: "1px solid #d1d5db",
  fontSize: 13,
  outline: "none",
  boxSizing: "border-box",
  background: disabled ? "#f9fafb" : "#fff",
  color: disabled ? "#9ca3af" : "#111827",
});

const btnBase: React.CSSProperties = {
  flex: 1,
  padding: "10px 0",
  borderRadius: 8,
  border: "1px solid #e5e7eb",
  background: "#fff",
  color: "#374151",
  fontSize: 13,
  fontWeight: 500,
  cursor: "pointer",
};


const FIELD_CONFIG = [
  { key: "full_name", label: "Full Name", required: false, type: "text", disabled: true },
  { key: "email", label: "Email", required: false, type: "email", disabled: true },
  { key: "phone", label: "Phone", required: false, type: "tel", disabled: true },
  { key: "current_company", label: "Current Company", required: false, type: "text", disabled: true },
  { key: "experience_years", label: "Experience (years)", required: false, type: "number", disabled: true },
  { key: "current_ctc", label: "Current CTC (₹)", required: false, type: "number", disabled: true },
  { key: "notice_period_days", label: "Notice Period (days)", required: false, type: "number", disabled: false },
  { key: "expected_ctc", label: "Expected CTC (₹)", required: true, type: "number", disabled: false },
] as const;


const ApplySidebar: React.FC<ApplySidebarProps> = ({
  jobId,
  job,
  companyInfo,
  careerPageUuid,
  primaryColor = "#4f46e5",
}) => {
  const guestToken = localStorage.getItem("guest_token");
  const isLoggedIn = !!guestToken;
  const primary = primaryColor;

  const [showAuthModal, setShowAuthModal] = useState(false);
  const [flowStarted, setFlowStarted] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [profile, setProfile] = useState<any>(null);
  const [profileLoading, setProfileLoading] = useState(false);

  const [selectedResumeId, setSelectedResumeId] = useState<string | null>(null);
  const [uploadingResume, setUploadingResume] = useState(false);
  const [newResumeFile, setNewResumeFile] = useState<File | null>(null);

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [joiningDate, setJoiningDate] = useState<Dayjs | null>(null);
  const [screeningAnswers, setScreeningAnswers] = useState<Record<string, string>>({});

  const [hasApplied, setHasApplied] = useState(false);
  const [existingApplication, setExistingApplication] = useState<{
    application_code: string;
    status: string;
    applied_at: string;
  } | null>(null);
  const [applyCheckLoading, setApplyCheckLoading] = useState(false);

  const screeningQuestions: ScreeningQuestion[] = useMemo(
    () => job?.screening_questions ?? [],
    [job]
  );


  const disablePastDates = useCallback(
    (date: Dayjs) => date.isBefore(dayjs().startOf("day")),
    []
  );


  const loadProfile = useCallback(async () => {
    if (!isLoggedIn) return;
    setProfileLoading(true);
    try {
      const data = await recruitmentApi.getCandidateProfile(
        companyInfo.company_id,
        companyInfo.company_type
      );
      setProfile(data);
      setForm({
        full_name: data.full_name ?? "",
        email: data.email ?? "",
        phone: data.phone ?? "",
        current_company: data.current_company ?? "",
        experience_years: data.total_experience_years ?? "",
        current_ctc: data.current_ctc ? String(Math.round(Number(data.current_ctc))) : "",
        expected_ctc: data.expected_ctc ? String(Math.round(Number(data.expected_ctc))) : "",
        notice_period_days: data.notice_period_days ? String(data.notice_period_days) : "",
        earliest_joining_date: "",
        cover_letter: "",
      });
      const primary = data.resumes?.find((r: Resume) => r.is_primary);
      if (primary) setSelectedResumeId(primary.id);
    } catch { } finally {
      setProfileLoading(false);
    }
  }, [isLoggedIn, companyInfo]);

  const checkApplication = useCallback(async () => {
    if (!isLoggedIn) return;
    setApplyCheckLoading(true);
    try {
      const appStatus = await recruitmentApi.checkApplicationStatus(
        companyInfo.company_id,
        companyInfo.company_type,
        jobId
      );
      setHasApplied(appStatus.has_applied);
      setExistingApplication(appStatus.application);
    } catch { } finally {
      setApplyCheckLoading(false);
    }
  }, [isLoggedIn, companyInfo, jobId]);

  useEffect(() => {
    if (isLoggedIn) {
      loadProfile();
      checkApplication();
    }
  }, [isLoggedIn, loadProfile, checkApplication]);


  const handleNewResumeUpload = async (file: File) => {
    setNewResumeFile(file);
    setUploadingResume(true);
    try {
      const fd = new FormData();
      fd.append("resume", file);
      const result = await recruitmentApi.uploadResume(fd);
      if (result?.id) {
        setSelectedResumeId(String(result.id));
        await loadProfile();
        notify.success("Resume uploaded!");
      }
    } catch (err: any) {
      notify.error(
        err?.data?.error?.message ?? err?.data?.message ?? err?.message ?? "Failed to upload resume"
      );
    } finally {
      setUploadingResume(false);
    }
  };


  const handleSubmit = async () => {
    if (!form.expected_ctc || !joiningDate) {
      notify.warn("Please fill Expected CTC and Earliest Joining Date.");
      return;
    }
    const requiredUnanswered = screeningQuestions.find(
      q => q.required && !screeningAnswers[q.question]
    );
    if (requiredUnanswered) {
      notify.warn(`Please answer: "${requiredUnanswered.question}"`);
      return;
    }

    setSubmitting(true);
    try {

      const ctcChanged =
        form.expected_ctc !== String(Math.round(Number(profile?.expected_ctc ?? 0)));
      if (ctcChanged && profile?.key_skills && profile?.education_history) {
        await recruitmentApi.createCandidateProfile(
          companyInfo.company_id,
          companyInfo.company_type,
          {
            ...profile,
            expected_ctc: Number(form.expected_ctc),
            notice_period_days: form.notice_period_days ? Number(form.notice_period_days) : null,
          }
        ).catch(() => { });
      }

      await recruitmentApi.submitApplication(
        companyInfo.company_id,
        companyInfo.company_type,
        {
          job_id: jobId,
          expected_ctc: Number(form.expected_ctc),
          earliest_joining_date: joiningDate.toISOString(),
          cover_letter: form.cover_letter || undefined,
          screening_answers:
            Object.keys(screeningAnswers).length > 0
              ? JSON.stringify(screeningAnswers)
              : undefined,
        }
      );
      setSubmitted(true);
    } catch (err: any) {
      notify.error(
        err?.data?.error?.message ?? err?.data?.message ?? err?.message ?? "Submission failed"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const setField = (key: keyof FormState, val: string) =>
    setForm(prev => ({ ...prev, [key]: val }));


  if (submitted) {
    return (
      <div style={{ ...cardStyle, padding: 28, textAlign: "center" }}>
        <div style={{
          width: 64, height: 64, borderRadius: "50%",
          background: "#f0fdf4", display: "flex",
          alignItems: "center", justifyContent: "center",
          margin: "0 auto 16px",
        }}>
          <i className="ti ti-circle-check" style={{ fontSize: 32, color: "#16a34a" }} />
        </div>
        <h5 style={{ fontWeight: 700, color: "#16a34a", marginBottom: 8 }}>Application Submitted!</h5>
        <p style={{ fontSize: 13, color: "#6b7280", marginBottom: 20 }}>
          We'll review your application and reach out soon. Track your status from the dashboard.
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {isLoggedIn && (
            <Link
              to={`/careers/${careerPageUuid}/profile`}
              style={{
                padding: "10px 20px", borderRadius: 8,
                background: primary, color: "#fff",
                textAlign: "center", textDecoration: "none",
                fontWeight: 600, fontSize: 14,
              }}
            >
              <i className="ti ti-layout-dashboard me-2" />Go to Dashboard
            </Link>
          )}
          <Link
            to={`/careers/${careerPageUuid}`}
            style={{
              padding: "10px 20px", borderRadius: 8,
              border: "1px solid #e5e7eb", color: "#374151",
              textAlign: "center", textDecoration: "none",
              fontWeight: 500, fontSize: 14,
            }}
          >
            Browse More Jobs
          </Link>
        </div>
      </div>
    );
  }

  if (isLoggedIn && !applyCheckLoading && hasApplied && existingApplication) {
    const statusColors: Record<string, { bg: string; border: string; text: string; icon: string }> = {
      applied: { bg: "#eff6ff", border: "#bfdbfe", text: "#1d4ed8", icon: "ti-clock-hour-4" },
      screening: { bg: "#fefce8", border: "#fde68a", text: "#92400e", icon: "ti-eye" },
      interview: { bg: "#f0fdf4", border: "#bbf7d0", text: "#15803d", icon: "ti-calendar-event" },
      offered: { bg: "#f5f3ff", border: "#ddd6fe", text: "#6d28d9", icon: "ti-gift" },
      hired: { bg: "#f0fdf4", border: "#bbf7d0", text: "#15803d", icon: "ti-circle-check" },
      rejected: { bg: "#fef2f2", border: "#fecaca", text: "#b91c1c", icon: "ti-x" },
    };

    const s = statusColors[existingApplication.status] ?? statusColors.applied;

    return (
      <div style={{ ...cardStyle, position: "sticky", top: 20 }}>
        <div style={{ height: 4, background: `linear-gradient(90deg, ${primary}, ${hexAlpha(primary, 0.4)})` }} />
        <div style={{ padding: 24 }}>


          <div style={{
            display: "flex", alignItems: "flex-start", gap: 12,
            background: s.bg, border: `1px solid ${s.border}`,
            borderRadius: 10, padding: "14px 16px", marginBottom: 20,
          }}>
            <i className={`ti ${s.icon}`} style={{ color: s.text, fontSize: 22, marginTop: 1, flexShrink: 0 }} />
            <div>
              <p style={{ fontSize: 14, fontWeight: 700, color: "#111827", margin: "0 0 2px" }}>
                You've already applied!
              </p>
              <p style={{ fontSize: 12, color: "#6b7280", margin: 0 }}>
                Applied on{" "}
                {new Date(existingApplication.applied_at).toLocaleDateString("en-IN", {
                  day: "numeric", month: "short", year: "numeric",
                })}
              </p>
            </div>
            <span style={{
              marginLeft: "auto", padding: "3px 10px", borderRadius: 20,
              background: s.border, color: s.text,
              fontSize: 11, fontWeight: 700, textTransform: "capitalize", whiteSpace: "nowrap",
            }}>
              {existingApplication.status}
            </span>
          </div>


          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            background: "#f9fafb", border: "1px solid #e5e7eb",
            borderRadius: 8, padding: "10px 14px", marginBottom: 16,
          }}>
            <div>
              <p style={{ fontSize: 11, color: "#9ca3af", margin: "0 0 2px", fontWeight: 500 }}>
                APPLICATION CODE
              </p>
              <p style={{ fontSize: 14, fontWeight: 700, color: "#111827", margin: 0, letterSpacing: 1 }}>
                {existingApplication.application_code}
              </p>
            </div>
            <button
              onClick={() => {
                navigator.clipboard.writeText(existingApplication.application_code);
                notify.success("Code copied!");
              }}
              style={{
                background: "none", border: "none", cursor: "pointer",
                color: primary, padding: 4,
              }}
              title="Copy code"
            >
              <i className="ti ti-copy" style={{ fontSize: 16 }} />
            </button>
          </div>


          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <Link
              to={`/careers/${careerPageUuid}/profile?tab=applications`}
              style={{
                padding: "10px 20px", borderRadius: 8,
                background: primary, color: "#fff",
                textAlign: "center", textDecoration: "none",
                fontWeight: 600, fontSize: 14,
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              }}
            >
              <i className="ti ti-layout-dashboard" style={{ fontSize: 15 }} />
              View in Dashboard
            </Link>
            <Link
              to={`/careers/${careerPageUuid}`}
              style={{
                padding: "10px 20px", borderRadius: 8,
                border: "1px solid #e5e7eb", color: "#374151",
                textAlign: "center", textDecoration: "none",
                fontWeight: 500, fontSize: 14,
              }}
            >
              Browse More Jobs
            </Link>
          </div>
        </div>
      </div>
    );
  }


  if (!flowStarted) {
    return (
      <>
        <div style={{ ...cardStyle, position: "sticky", top: 20 }}>
          <div style={{ height: 4, background: `linear-gradient(90deg, ${primary}, ${hexAlpha(primary, 0.4)})` }} />
          <div style={{ padding: 24 }}>

            {isLoggedIn && (profileLoading || applyCheckLoading) ? (
              <div style={{ textAlign: "center", padding: "24px 0" }}>
                <Spin size="small" />
                <p style={{ marginTop: 10, color: "#9ca3af", fontSize: 13 }}>Loading your profile...</p>
              </div>
            ) : isLoggedIn && !profile ? (
              <div style={{ textAlign: "center", marginBottom: 20 }}>
                <div style={{
                  width: 56, height: 56, borderRadius: "50%",
                  background: "#fffbeb", border: "1px solid #fde68a",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  margin: "0 auto 14px",
                }}>
                  <i className="ti ti-alert-triangle" style={{ fontSize: 26, color: "#d97706" }} />
                </div>
                <h6 style={{ fontWeight: 700, color: "#92400e", marginBottom: 8 }}>Profile Not Found</h6>
                <p style={{ fontSize: 13, color: "#78350f", marginBottom: 16 }}>
                  Please create your profile first from the profile dashboard to apply for this job.
                </p>
                <Link
                  to={`/careers/${careerPageUuid}/profile`}
                  style={{
                    display: "inline-block", padding: "9px 20px", borderRadius: 8,
                    background: "#f59e0b", color: "#fff", textDecoration: "none",
                    fontWeight: 600, fontSize: 14,
                  }}
                >
                  Go to Profile Dashboard
                </Link>
              </div>
            ) : isLoggedIn && profile ? (
              <>

                <div style={{
                  display: "flex", alignItems: "center", gap: 12,
                  background: "#fafbfc", borderRadius: 10,
                  border: "1px solid #e8eaed",
                  padding: "12px 14px", marginBottom: 16,
                }}>
                  {profile.profile_pic_serve_url ? (
                    <img
                      src={profile.profile_pic_serve_url}
                      alt={profile.full_name}
                      style={{
                        width: 44, height: 44, borderRadius: "50%",
                        objectFit: "cover", border: `2px solid ${hexAlpha(primary, 0.3)}`,
                      }}
                    />
                  ) : (
                    <div style={{
                      width: 44, height: 44, borderRadius: "50%",
                      background: hexAlpha(primary, 0.12),
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontWeight: 700, fontSize: 18, color: primary,
                    }}>
                      {profile.full_name?.[0]?.toUpperCase()}
                    </div>
                  )}
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14, color: "#111827" }}>{profile.full_name}</div>
                    <div style={{ fontSize: 12, color: "#6b7280" }}>
                      {profile.current_designation ?? profile.email}
                    </div>
                  </div>
                  <div style={{
                    marginLeft: "auto", padding: "3px 10px", borderRadius: 20,
                    background: "#f0fdf4", color: "#16a34a",
                    fontSize: 11, fontWeight: 600,
                  }}>
                    {profile.profile_completion_percentage}% complete
                  </div>
                </div>

                <div style={{ marginBottom: 16 }}>
                  <p style={{ fontSize: 13, color: "#6b7280", marginBottom: 12 }}>
                    Your profile is ready. Click below to apply — your details will be auto-filled.
                  </p>
                  {profile.key_skills?.length > 0 && (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                      {profile.key_skills.slice(0, 4).map((s: string) => (
                        <span key={s} style={{
                          padding: "3px 10px", borderRadius: 20,
                          background: hexAlpha(primary, 0.08),
                          color: primary, fontSize: 11, fontWeight: 500,
                        }}>{s}</span>
                      ))}
                      {profile.key_skills.length > 4 && (
                        <span style={{ padding: "3px 10px", color: "#9ca3af", fontSize: 11 }}>
                          +{profile.key_skills.length - 4} more
                        </span>
                      )}
                    </div>
                  )}
                </div>

                <button
                  onClick={() => setFlowStarted(true)}
                  style={{
                    width: "100%", padding: "12px 0", borderRadius: 9, border: "none",
                    background: `linear-gradient(135deg, ${primary}, ${hexAlpha(primary, 0.8)})`,
                    color: "#fff", fontWeight: 700, fontSize: 15, cursor: "pointer",
                    boxShadow: `0 4px 14px ${hexAlpha(primary, 0.35)}`,
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                  }}
                >
                  <i className="ti ti-rocket" style={{ fontSize: 16 }} />
                  Apply for this Role
                </button>
              </>
            ) : (
              <>
                <div style={{ textAlign: "center", marginBottom: 20 }}>
                  <div style={{
                    width: 56, height: 56, borderRadius: "50%",
                    background: hexAlpha(primary, 0.1),
                    display: "flex", alignItems: "center", justifyContent: "center",
                    margin: "0 auto 14px",
                  }}>
                    <i className="ti ti-rocket" style={{ fontSize: 26, color: primary }} />
                  </div>
                  <h6 style={{ fontWeight: 700, color: "#111827", marginBottom: 8 }}>Apply for this Role</h6>
                  <p style={{ fontSize: 13, color: "#6b7280", marginBottom: 0 }}>
                    Login or create a profile to apply faster and track your application.
                  </p>
                </div>

                <button
                  onClick={() => setShowAuthModal(true)}
                  style={{
                    width: "100%", padding: "11px 0", borderRadius: 9, border: "none",
                    background: `linear-gradient(135deg, ${primary}, ${hexAlpha(primary, 0.8)})`,
                    color: "#fff", fontWeight: 700, fontSize: 14, cursor: "pointer",
                    boxShadow: `0 4px 14px ${hexAlpha(primary, 0.35)}`,
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                    marginBottom: 10,
                  }}
                >
                  <i className="ti ti-user-circle" style={{ fontSize: 16 }} />
                  Login / Create Profile
                </button>
              </>
            )}
          </div>


          <div style={{ borderTop: "1px solid #f0f0f0", padding: "12px 20px", background: "#fafbfc" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
              {job?.posted_date && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#6b7280" }}>
                  <i className="ti ti-calendar" style={{ fontSize: 14 }} />
                  Posted {new Date(job.posted_date).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                </div>
              )}
              {job?.openings_count && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#6b7280" }}>
                  <i className="ti ti-users" style={{ fontSize: 14 }} />
                  {job.openings_count} opening{job.openings_count > 1 ? "s" : ""}
                </div>
              )}
              {job?.applications_count !== undefined && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#6b7280" }}>
                  <i className="ti ti-file-text" style={{ fontSize: 14 }} />
                  {job.applications_count} application{job.applications_count !== 1 ? "s" : ""} received
                </div>
              )}
            </div>
          </div>
        </div>

        <GuestAuthModal
          show={showAuthModal}
          onHide={() => setShowAuthModal(false)}
          companyId={companyInfo.company_id}
          companyType={companyInfo.company_type}
          onSuccess={() => {
            setShowAuthModal(false);
          }}
        />
      </>
    );
  }


  const steps = isLoggedIn
    ? ["Resume", "Review & Apply", "Done"]
    : ["Your Info", "Done"];

  return (
    <div style={cardStyle}>

      <div style={{ height: 4, background: `linear-gradient(90deg, ${primary}, ${hexAlpha(primary, 0.4)})` }} />

      <div style={{ padding: "18px 20px 0" }}>

        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 20 }}>
          {steps.map((step, i) => {
            const done = i < currentStep;
            const active = i === currentStep;
            return (
              <React.Fragment key={step}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <div style={{
                    width: 24, height: 24, borderRadius: "50%",
                    background: done ? "#16a34a" : active ? primary : "#e5e7eb",
                    color: done || active ? "#fff" : "#9ca3af",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 11, fontWeight: 700, flexShrink: 0,
                  }}>
                    {done ? <i className="ti ti-check" style={{ fontSize: 11 }} /> : i + 1}
                  </div>
                  <span style={{
                    fontSize: 12,
                    fontWeight: active ? 600 : 400,
                    color: active ? "#111827" : done ? "#16a34a" : "#9ca3af",
                  }}>{step}</span>
                </div>
                {i < steps.length - 1 && (
                  <div style={{ flex: 1, height: 1, background: i < currentStep ? "#16a34a" : "#e5e7eb" }} />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      <div style={{ padding: "0 20px 20px" }}>


        {isLoggedIn && currentStep === 0 && (
          <div>
            <p style={{ fontSize: 13, color: "#6b7280", marginBottom: 14 }}>
              Select a resume to attach to your application:
            </p>

            {profile?.resumes?.length > 0 ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 14 }}>
                {profile.resumes.map((r: Resume) => {
                  const selected = selectedResumeId === r.id;
                  return (
                    <div
                      key={r.id}
                      onClick={() => setSelectedResumeId(r.id)}
                      style={{
                        display: "flex", alignItems: "center", gap: 10,
                        padding: "10px 12px", borderRadius: 8, cursor: "pointer",
                        border: `1.5px solid ${selected ? primary : "#e5e7eb"}`,
                        background: selected ? hexAlpha(primary, 0.05) : "#fafbfc",
                        transition: "all 0.15s",
                      }}
                    >
                      <div style={{
                        width: 32, height: 32, borderRadius: 6,
                        background: selected ? hexAlpha(primary, 0.12) : "#f4f5f7",
                        display: "flex", alignItems: "center", justifyContent: "center",
                      }}>
                        <i className="ti ti-file-type-pdf" style={{ color: selected ? primary : "#9ca3af", fontSize: 16 }} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 500, color: "#111827", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {r.file_name}
                        </div>
                        <div style={{ fontSize: 11, color: "#9ca3af" }}>
                          {r.is_primary && <span style={{ color: "#16a34a", marginRight: 6 }}>★ Primary</span>}
                          {new Date(r.uploaded_at).toLocaleDateString("en-IN")}
                        </div>
                      </div>
                      {selected && <i className="ti ti-circle-check-filled" style={{ color: primary, fontSize: 18 }} />}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p style={{ fontSize: 13, color: "#9ca3af", marginBottom: 14 }}>No resumes found. Upload one below.</p>
            )}


            <div
              onClick={() => document.getElementById("new-resume-input")?.click()}
              style={{
                border: "1.5px dashed #d1d5db", borderRadius: 8,
                padding: 12, textAlign: "center",
                cursor: "pointer", background: "#fafbfc", marginBottom: 16,
              }}
            >
              {uploadingResume ? (
                <><Spin size="small" /><span style={{ fontSize: 12, color: "#6b7280", marginLeft: 8 }}>Uploading...</span></>
              ) : newResumeFile && selectedResumeId ? (
                <span style={{ fontSize: 12, color: "#16a34a" }}>
                  <i className="ti ti-check me-1" />{newResumeFile.name}
                </span>
              ) : (
                <>
                  <i className="ti ti-cloud-upload" style={{ fontSize: 20, color: "#9ca3af", display: "block", marginBottom: 4 }} />
                  <span style={{ fontSize: 12, color: "#6b7280" }}>Upload new resume (PDF, DOC, max 5MB)</span>
                </>
              )}
            </div>
            <input
              id="new-resume-input" type="file" accept=".pdf,.doc,.docx"
              style={{ display: "none" }}
              onChange={e => { const f = e.target.files?.[0]; if (f) handleNewResumeUpload(f); }}
            />

            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => setFlowStarted(false)} style={btnBase}>Back</button>
              <button
                onClick={() => {
                  if (!selectedResumeId) return notify.warn("Please select or upload a resume to continue.");
                  setCurrentStep(1);
                }}
                style={{
                  flex: 2, padding: "10px 0", borderRadius: 8, border: "none",
                  background: primary, color: "#fff", fontSize: 14, fontWeight: 600, cursor: "pointer",
                }}
              >Continue →</button>
            </div>
          </div>
        )}


        {currentStep === (isLoggedIn ? 1 : 0) && (
          <div>
            {isLoggedIn && (
              <div style={{
                display: "flex", alignItems: "center", gap: 6,
                padding: "8px 12px", borderRadius: 7, marginBottom: 14,
                background: "#f0fdf4", border: "1px solid #bbf7d0",
                fontSize: 12, color: "#16a34a", fontWeight: 500,
              }}>
                <i className="ti ti-sparkles" style={{ fontSize: 14 }} />
                Fields auto-filled from your profile — review before submitting
              </div>
            )}


            {FIELD_CONFIG.map(({ key, label, required, type, disabled }) => (
              <div key={key} style={{ marginBottom: 10 }}>
                <label style={{ fontSize: 12, fontWeight: 500, color: "#374151", marginBottom: 4, display: "block" }}>
                  {label} {required && <span style={{ color: "#ef4444" }}>*</span>}
                </label>
                <input
                  type={type}
                  value={(form as any)[key]}
                  onChange={e => !disabled && setField(key as keyof FormState, e.target.value)}
                  disabled={disabled}
                  style={inputStyle(disabled, primary)}
                  onFocus={e => !disabled && (e.target.style.borderColor = primary)}
                  onBlur={e => !disabled && (e.target.style.borderColor = "#d1d5db")}
                />
              </div>
            ))}


            <div style={{ marginBottom: 10 }}>
              <label style={{ fontSize: 12, fontWeight: 500, color: "#374151", marginBottom: 4, display: "block" }}>
                Earliest Joining Date <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <DatePicker
                value={joiningDate}
                onChange={date => setJoiningDate(date)}
                disabledDate={disablePastDates}
                format="DD MMM YYYY"
                placeholder="Select a future date"
                style={{ width: "100%", borderRadius: 7, fontSize: 13 }}
                allowClear
              />
            </div>


            {screeningQuestions.length > 0 && (
              <div style={{ marginTop: 16, paddingTop: 14, borderTop: "1px solid #f0f0f0" }}>
                <p style={{ fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 12 }}>
                  Screening Questions
                </p>
                {screeningQuestions.map((q, i) => (
                  <div key={i} style={{ marginBottom: 14 }}>
                    <label style={{ fontSize: 12, fontWeight: 500, color: "#374151", marginBottom: 6, display: "block", lineHeight: 1.4 }}>
                      {q.question} {q.required && <span style={{ color: "#ef4444" }}>*</span>}
                    </label>
                    {q.type === "multiple_choice" ? (
                      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        {q.options.map(opt => (
                          <label key={opt} style={{
                            display: "flex", alignItems: "center", gap: 8,
                            padding: "7px 10px", borderRadius: 7, cursor: "pointer",
                            border: `1.5px solid ${screeningAnswers[q.question] === opt ? primary : "#e5e7eb"}`,
                            background: screeningAnswers[q.question] === opt ? hexAlpha(primary, 0.05) : "#fff",
                            fontSize: 13,
                          }}>
                            <input
                              type="radio"
                              name={`sq-${i}`}
                              value={opt}
                              checked={screeningAnswers[q.question] === opt}
                              onChange={() => setScreeningAnswers(prev => ({ ...prev, [q.question]: opt }))}
                              style={{ accentColor: primary }}
                            />
                            {opt}
                          </label>
                        ))}
                      </div>
                    ) : (
                      <textarea
                        value={screeningAnswers[q.question] ?? ""}
                        onChange={e => setScreeningAnswers(prev => ({ ...prev, [q.question]: e.target.value }))}
                        rows={2}
                        style={{
                          width: "100%", padding: "7px 10px", borderRadius: 7,
                          border: "1px solid #d1d5db", fontSize: 13,
                          resize: "vertical", boxSizing: "border-box",
                        }}
                        onFocus={e => (e.target.style.borderColor = primary)}
                        onBlur={e => (e.target.style.borderColor = "#d1d5db")}
                      />
                    )}
                  </div>
                ))}
              </div>
            )}


            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 12, fontWeight: 500, color: "#374151", marginBottom: 4, display: "block" }}>
                Cover Letter <span style={{ fontSize: 11, color: "#9ca3af", fontWeight: 400 }}>(optional)</span>
              </label>
              <textarea
                rows={3}
                value={form.cover_letter}
                onChange={e => setField("cover_letter", e.target.value)}
                placeholder="Why are you a great fit for this role?"
                style={{
                  width: "100%", padding: "7px 10px", borderRadius: 7,
                  border: "1px solid #d1d5db", fontSize: 13,
                  resize: "vertical", boxSizing: "border-box",
                }}
                onFocus={e => (e.target.style.borderColor = primary)}
                onBlur={e => (e.target.style.borderColor = "#d1d5db")}
              />
            </div>

            <div style={{ display: "flex", gap: 8 }}>
              <button
                onClick={() => isLoggedIn ? setCurrentStep(0) : setFlowStarted(false)}
                style={btnBase}
              >Back</button>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                style={{
                  flex: 2, padding: "11px 0", borderRadius: 8, border: "none",
                  background: submitting ? "#9ca3af" : primary,
                  color: "#fff", fontSize: 14, fontWeight: 700,
                  cursor: submitting ? "not-allowed" : "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                }}
              >
                {submitting
                  ? <><span className="spinner-border spinner-border-sm" /> Submitting...</>
                  : <><i className="ti ti-send" style={{ fontSize: 14 }} />Submit Application</>
                }
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ApplySidebar;